/**
 * Inventory → Index: consultable catalog of all published cosmetics
 * (owned = full color; unowned = black silhouette with hover reveal).
 */
import { parseLimitedDurationMs } from "../../lib/limitedDuration";
import type { CosmeticCategory, ShopCosmeticDto, ShopCrateDto } from "../lib/cloud";

export type IndexFilter = "all" | "obtained" | "not_obtained";

export const INDEX_FILTER_OPTIONS: { value: IndexFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "obtained", label: "Obtained" },
  { value: "not_obtained", label: "Not Obtained" },
];

const SCORE_MODE_LABELS: Record<string, string> = {
  normal: "Normal",
  wasd: "WASD",
  superdot: "Superdot",
  progression: "Progression",
  roulette: "Roulette",
  sandbox: "Sandbox",
  speedrun: "SpeedRun",
  highway: "Highway",
  darkness: "Darkness",
  mirror: "Mirror",
  snake: "Snake",
  combo: "Combo",
  overload: "Overload",
  colorrush: "Color Rush",
  heat: "Heat",
  hunter: "Hunter",
  pop: "POP",
  counter: "Counter",
  aim: "AIM",
  hook: "Hook",
  tilt: "Tilt",
  orbit: "Orbit",
  delay: "Delay",
  penalty: "Golf",
};

export type IndexCrateSource = {
  crateId: string;
  crateName: string;
  probabilityPercent: number;
};

/** Map cosmeticId → crate drop sources (published crates only). */
export function buildCrateSourceMap(crates: ShopCrateDto[]): Map<string, IndexCrateSource[]> {
  const map = new Map<string, IndexCrateSource[]>();
  for (const crate of crates) {
    const crateId = String(crate.id || "").trim();
    const name = String(crate.name || "Crate").trim() || "Crate";
    for (const entry of crate.contents ?? []) {
      const id = entry.cosmeticId;
      if (!id) continue;
      const list = map.get(id) ?? [];
      // Same cosmetic can only appear once per crate (DB PK); still guard client-side.
      if (crateId && list.some((s) => s.crateId === crateId)) continue;
      list.push({
        crateId: crateId || name,
        crateName: name,
        probabilityPercent: Number(entry.probabilityPercent) || 0,
      });
      map.set(id, list);
    }
  }
  return map;
}

function formatProb(p: number): string {
  const n = Number(p);
  if (!Number.isFinite(n)) return "0%";
  const rounded = Math.round(n * 100) / 100;
  return `${rounded}%`;
}

function shopUnlockLine(item: ShopCosmeticDto): string {
  if (item.unlockType === "dots") {
    const price = Math.max(0, Math.floor(Number(item.priceDots) || 0));
    return `Value: ${price.toLocaleString()} Dots`;
  }
  if (item.unlockType === "free") return "Free";
  if (item.unlockType === "score") {
    if (item.unlockScoreMode && item.unlockScoreValue != null) {
      const mode = SCORE_MODE_LABELS[item.unlockScoreMode] || item.unlockScoreMode;
      const n = Number(item.unlockScoreValue).toLocaleString();
      if (item.unlockScoreMode === "progression") return `Level ${n} in ${mode}`;
      return `Score ${n} in ${mode}`;
    }
    if (item.unlockLabel && item.unlockLabel !== "Score required") return item.unlockLabel;
    return "Score required";
  }
  if (item.unlockLabel) return item.unlockLabel;
  if (item.unlockType === "achievement") return "Complete an achievement";
  if (item.unlockType === "challenge") {
    return item.unlockChallengeId ? `Challenge: ${item.unlockChallengeId}` : "Complete a challenge";
  }
  return "Special unlock";
}

function formatLimitedWindow(item: ShopCosmeticDto): string | null {
  if (!item.limitedItem) return null;
  const durationText = String(item.limitedDurationText || "").trim();
  const endsMs = item.limitedEndsAt ? Date.parse(item.limitedEndsAt) : NaN;
  let fromLabel = "";
  if (Number.isFinite(endsMs) && durationText) {
    const durMs = parseLimitedDurationMs(durationText);
    if (durMs) {
      const startMs = endsMs - durMs;
      fromLabel = new Date(startMs).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }
  if (!fromLabel && item.createdAt) {
    const created = Date.parse(item.createdAt);
    if (Number.isFinite(created)) {
      fromLabel = new Date(created).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  }
  const forPart = durationText ? ` for ${durationText}` : "";
  if (fromLabel) return `Limited: from ${fromLabel}${forPart}`;
  if (durationText) return `Limited: available for ${durationText}`;
  return "Limited edition";
}

/** How to obtain this cosmetic (one or more short lines). */
export function buildAcquisitionLines(
  item: ShopCosmeticDto,
  crateSources: IndexCrateSource[] | undefined,
): string[] {
  const lines: string[] = [];
  const crates = crateSources ?? [];

  if (item.campaignOnly || item.campaignUnlockLabel) {
    lines.push(
      item.campaignUnlockLabel
        ? `Campaign: ${item.campaignUnlockLabel}`
        : "Campaign reward",
    );
  } else if (item.taskOnly) {
    lines.push("Task reward");
  } else if (item.battlePassOnly) {
    lines.push("Battle Pass reward");
  } else if (item.crateOnly) {
    if (crates.length) {
      for (const src of crates) {
        lines.push(`${src.crateName} — ${formatProb(src.probabilityPercent)}`);
      }
    } else {
      lines.push("Obtainable from crates");
    }
  } else if (item.sentOnly) {
    lines.push("Granted by admin");
  } else {
    lines.push(shopUnlockLine(item));
  }

  const limited = formatLimitedWindow(item);
  if (limited) lines.push(limited);

  return lines.length ? lines : ["Unknown source"];
}

/**
 * Index catalog: unique by id, and crate-only cosmetics must appear in at least
 * one published crate (orphans left after republish/remove look like duplicates).
 * Owned orphans still show so the player's collection stays complete.
 */
export function filterIndexCatalog(
  items: ShopCosmeticDto[],
  crateSources: Map<string, IndexCrateSource[]>,
  ownedIds?: Set<string>,
): ShopCosmeticDto[] {
  const seen = new Set<string>();
  const out: ShopCosmeticDto[] = [];
  for (const item of items) {
    if (!item?.id || seen.has(item.id)) continue;
    seen.add(item.id);
    if (item.crateOnly) {
      const sources = crateSources.get(item.id);
      if (!sources?.length && !(ownedIds && ownedIds.has(item.id))) continue;
    }
    out.push(item);
  }
  return out;
}

function createdAtMs(item: ShopCosmeticDto): number {
  const t = item.createdAt ? Date.parse(item.createdAt) : NaN;
  return Number.isFinite(t) ? t : 0;
}

/** Filter Index list; always alphabetical within the result. */
export function filterIndexItems(
  items: ShopCosmeticDto[],
  filter: IndexFilter,
  ownedIds: Set<string>,
): ShopCosmeticDto[] {
  let list = [...items];
  if (filter === "obtained") list = list.filter((i) => ownedIds.has(i.id));
  else if (filter === "not_obtained") list = list.filter((i) => !ownedIds.has(i.id));
  list.sort((a, b) => {
    const byName = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    if (byName) return byName;
    return createdAtMs(a) - createdAtMs(b);
  });
  return list;
}

export function groupIndexByCategory(
  items: ShopCosmeticDto[],
  categoryOf: (item: ShopCosmeticDto) => CosmeticCategory,
  order: CosmeticCategory[],
): { category: CosmeticCategory; items: ShopCosmeticDto[] }[] {
  const out: { category: CosmeticCategory; items: ShopCosmeticDto[] }[] = [];
  for (const cat of order) {
    const group = items.filter((i) => categoryOf(i) === cat);
    if (group.length) out.push({ category: cat, items: group });
  }
  return out;
}
